import { useMemo, useCallback } from "react"
import { map, chain, compact } from "lodash"
import { useOutletContext } from "react-router-dom"
import { percentageFormatter, oneDecimalPercentageFormatter } from "helpers/formatters/numbers"
import { createPerformanceCumulativeSingleDateQuery } from "gatherers/single-date/cumulative-performance"
import { createPerformanceCumulativeMultiDateQuery, METRICS } from "gatherers/multi-date/cumulative-performance"
import { createPeriodPerformanceSingleDateQuery, ANNUALIZED_RETURNS, ANNUALIZED_VOLATILITY, MAX_DRAWDOWN, RETURNS_CORRELATION, SHARPE_RATIO, SORTINO_RATIO, FACTORS_ANNUALIZED_RETURNS, FACTORS_ANNUALIZED_VOLATILITY, FACTORS_SHARPE_RATIO, ALPHA_ANNUALIZED_RETURNS, ALPHA_ANNUALIZED_VOLATILITY, ALPHA_SHARPE_RATIO, TOTAL_RETURN, ALPHA_RETURN, FACTORS_RETURN, TRADING_RETURN, FACTOR_ANNUALIZED_RETURNS, FACTOR_ANNUALIZED_VOLATILITY, FACTOR_SHARPE_RATIO, BATTING_ALPHA, BATTING_TOTAL, BATTING_FACTORS, SLUGGING_ALPHA, SLUGGING_TOTAL, SLUGGING_FACTORS } from "gatherers/single-date/period-performance"
import { useIsBattingSluggingEnabled } from "resources/account"
import CompareGraph from "../renderers/compare-graph"
import { useSelectedMetric } from "../hooks/selected-metric"
import { useAvailableMetrics } from "../hooks/available-metrics"
import DataIntegratedSection from "./data-integrated-section"
import { transformDecisionQualityLabels } from "../helpers/transform-decision-quality-labels"

export default function ComparePerformance() {
  const context = useOutletContext()
  const { modelMetadata, modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric, modelMetrics, contextBase } = context
  const isBattingSluggingEnabled = useIsBattingSluggingEnabled()

  const specificMetrics = {
    id: "cumulative",
    label: "Cumulative Performance",
    options: map(METRICS, ({ label }, id) => ({ label, id, category: "cumulative" })),
  }
  const availableMetrics = useAvailableMetrics(modelMetrics, specificMetrics, true)
  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)
  const isFactorMetric = useCallback((metric) => {
    return chain(modelMetrics)
      .map(({ options }) => options)
      .flatten()
      .find(({ id }) => id === metric.id)
      .value()
  }, [modelMetrics])

  const currentDate = selectedDate || endDate

  const battingSluggingTotalMetrics = useMemo(
    () => (isBattingSluggingEnabled ? [BATTING_TOTAL, SLUGGING_TOTAL] : []),
    [isBattingSluggingEnabled],
  )
  const battingSluggingAlphaMetrics = useMemo(
    () => (isBattingSluggingEnabled ? [BATTING_ALPHA, SLUGGING_ALPHA] : []),
    [isBattingSluggingEnabled],
  )
  const battingSluggingFactorsMetrics = useMemo(
    () => (isBattingSluggingEnabled ? [BATTING_FACTORS, SLUGGING_FACTORS] : []),
    [isBattingSluggingEnabled],
  )

  const graph = useMemo(() => ({
    id: "graph",
    name: "Graph",
    renderer: CompareGraph,
    metrics: map(positionSets, positionSet => createPerformanceCumulativeMultiDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, startDate, endDate)),
    availableMetrics,
    selectedMetric,
    positionSetsMetadata: positionSets,
    tickFormatter: oneDecimalPercentageFormatter,
    separator: true,
  }), [positionSets, availableMetrics, selectedMetric, modelId, startDate, endDate])

  const sections = useMemo(
    () => compact([
      graph,
      {
        id: "total",
        name: "Total Return",
        defaultExpanded: true,
        separator: true,
        dataTransform: transformDecisionQualityLabels,
        metrics: map(positionSets, positionSet => createPeriodPerformanceSingleDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, positionSets[0].queryPositionSet, startDate, currentDate, [
          TOTAL_RETURN, ANNUALIZED_RETURNS, ANNUALIZED_VOLATILITY, SHARPE_RATIO, SORTINO_RATIO, RETURNS_CORRELATION, MAX_DRAWDOWN, ...battingSluggingTotalMetrics,
        ], { contextBase }),
        ),
        rowCount: 7 + battingSluggingTotalMetrics.length,
      }, {
        id: "alpha",
        name: "Alpha",
        dataTransform: transformDecisionQualityLabels,
        metrics: map(positionSets, positionSet =>
          createPeriodPerformanceSingleDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, positionSets[0].queryPositionSet, startDate, currentDate, [
            ALPHA_RETURN, ALPHA_ANNUALIZED_RETURNS, ALPHA_ANNUALIZED_VOLATILITY, ALPHA_SHARPE_RATIO, ...battingSluggingAlphaMetrics,
          ], { contextBase }),
        ),
        rowCount: 4 + battingSluggingAlphaMetrics.length,
      }, {
        id: "factors",
        name: "Factors",
        separator: true,
        dataTransform: transformDecisionQualityLabels,
        metrics: map(positionSets, positionSet =>
          createPeriodPerformanceSingleDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, positionSets[0].queryPositionSet, startDate, currentDate, [
            FACTORS_RETURN, FACTORS_ANNUALIZED_RETURNS, FACTORS_ANNUALIZED_VOLATILITY, FACTORS_SHARPE_RATIO, ...battingSluggingFactorsMetrics,
          ], { contextBase }),
        ),
        rowCount: 4 + battingSluggingFactorsMetrics.length,
      }, {
        id: "trading",
        name: "Trading",
        separator: true,
        metrics: map(positionSets, positionSet =>
          createPeriodPerformanceSingleDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, positionSets[0].queryPositionSet, startDate, currentDate, [
            TRADING_RETURN,
          ], { contextBase }),
        ),
      },
      isFactorMetric(selectedMetric) && {
        id: `selected-metric-${selectedMetric.id}`,
        name: selectedMetric.label,
        downloadName: `${selectedMetric.label} Performance`,
        separator: true,
        metrics: map(positionSets, positionSet => createPeriodPerformanceSingleDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, positionSets[0].queryPositionSet, startDate, currentDate, [
          FACTOR_ANNUALIZED_RETURNS, FACTOR_ANNUALIZED_VOLATILITY, FACTOR_SHARPE_RATIO,
        ], { factorId: selectedMetric.id })),
      },
      ...map(modelMetadata.categories, category => ({
        id: category.id,
        name: category.label,
        color: category.color,
        isGroup: true,
        sorted: true,
        metrics: map(positionSets, positionSet => createPerformanceCumulativeSingleDateQuery(modelId, positionSet.queryPositionSet, positionSet.queryBase, startDate, currentDate, { category: category.id })),
      })),
    ]),
    // [TODO] how to improve reference integrity of position sets
    [graph, positionSets, isFactorMetric, selectedMetric, modelMetadata?.categories, modelId, startDate, currentDate, contextBase, battingSluggingTotalMetrics, battingSluggingAlphaMetrics, battingSluggingFactorsMetrics],
  )

  return sections.map((section, index) => {
    const configKey = `performance-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={percentageFormatter}
      />
    )
  })
}
